import React, { useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import {
  MdCheckCircle,
  MdClose,
  MdExpandLess,
  MdExpandMore,
  MdOutlineAssessment,
  MdOutlineCreditScore
} from 'react-icons/md'
import { actions } from '../../store'
import Navbar from './Navbar'

export default function LoanReviews() {
  const dispatch = useDispatch()
  const { url, loanReviews = [] } = useSelector(state => state)

  const [expandedId, setExpandedId] = useState(null)
  const [reviewingId, setReviewingId] = useState(null)
  const [reviewDecision, setReviewDecision] = useState(null)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const formatCurrency = amount =>
    `₦${Number(amount || 0).toLocaleString()}`

  const formatPercentage = probability =>
    `${(Number(probability || 0) * 100).toFixed(1)}%`

  const openReview = (id, decision) => {
    setReviewingId(id)
    setReviewDecision(decision)
    setReason('')
    setError('')
  }

  const closeReview = () => {
    setReviewingId(null)
    setReviewDecision(null)
    setReason('')
    setError('')
  }

  const submitReview = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for your decision.')
      return
    }

    try {
      setLoading(true)
      setError('')

      await axios.patch(
        `${url}/admin/loan-applications/${reviewingId}/review`,
        {
          decision: reviewDecision,
          reason: reason.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      )

      dispatch(
        actions.updateLoanReviews(
          loanReviews.filter(
            application => application.loan_application_id !== reviewingId
          )
        )
      )

      closeReview()
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.detail ||
        'Unable to complete the review.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen w-full bg-[#F7F9FC] font-[Satoshi]">
      <Navbar />

      <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-green-600">UNDERWRITING</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-900">Loan reviews</h1>
              <p className="mt-2 text-sm text-gray-500">
                Applications that require a human underwriting decision.
              </p>
            </div>

            <div className="w-fit rounded-full border border-amber-100 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-700">
              {loanReviews.length} awaiting review
            </div>
          </div>

          {loanReviews.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-600">
                <MdCheckCircle className="text-2xl" />
              </div>
              <p className="mt-4 text-lg font-semibold text-gray-800">Review queue is clear</p>
              <p className="mt-2 text-sm text-gray-400">No applications currently require manual review.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {loanReviews.map(application => {
                const assessment = application.assessment || {}
                const creditAnalysis = assessment.credit_analysis || {}
                const loans = Array.isArray(creditAnalysis.loans)
                  ? creditAnalysis.loans
                  : []
                const totalRepayment = Number(application.rent_amount || 0) * 1.2
                const isExpanded = expandedId === application.loan_application_id
                const isReviewing = reviewingId === application.loan_application_id

                return (
                  <article
                    key={application.loan_application_id}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                  >
                    <div className="p-5 sm:p-6">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
                              Under review
                            </span>
                            <span className="text-xs text-gray-400">
                              Application #{application.loan_application_id}
                            </span>
                          </div>

                          <h2 className="mt-3 break-all text-lg font-bold text-gray-900">
                            {application.email}
                          </h2>

                          <p className="mt-1 text-sm text-gray-500">
                            {application.property_address || 'Property address unavailable'}
                          </p>
                        </div>

                        <div className="rounded-xl bg-green-50 px-4 py-3 lg:text-right">
                          <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Rent requested</p>
                          <p className="mt-1 text-xl font-bold text-gray-900">
                            {formatCurrency(application.rent_amount)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <Metric
                          label="Credit score"
                          value={`${assessment.credit_score ?? '—'}/100`}
                        />
                        <Metric
                          label="AI risk"
                          value={assessment.ai_risk_level || '—'}
                          capitalize
                        />
                        <Metric
                          label="Risk probability"
                          value={formatPercentage(assessment.ai_risk_probability)}
                        />
                        <Metric
                          label="Recommended limit"
                          value={formatCurrency(assessment.recommended_credit_limit)}
                        />
                        <Metric
                          label="Exposure"
                          value={formatPercentage(assessment.exposure_ratio)}
                        />
                        <Metric
                          label="Total obligation"
                          value={formatCurrency(totalRepayment)}
                        />
                      </div>

                      <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-gray-600 shadow-sm">
                            <MdOutlineAssessment className="text-xl" />
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Underwriting reason</p>
                            <p className="mt-1 text-sm leading-6 text-gray-700">
                              {assessment.decision_reason || 'No underwriting reason was provided.'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : application.loan_application_id)}
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                        >
                          <MdOutlineCreditScore className="text-lg" />
                          {isExpanded ? 'Hide credit details' : 'View credit details'}
                          {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                        </button>

                        <button
                          type="button"
                          onClick={() => openReview(application.loan_application_id, 'approved')}
                          className="h-11 rounded-xl bg-green-600 px-5 text-sm font-semibold text-white transition hover:bg-green-700"
                        >
                          Approve application
                        </button>

                        <button
                          type="button"
                          onClick={() => openReview(application.loan_application_id, 'rejected')}
                          className="h-11 rounded-xl border border-red-200 bg-red-50 px-5 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                        >
                          Reject application
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-gray-200 bg-gray-50/70 p-5 sm:p-6">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                          <SmallStat label="Total loans" value={creditAnalysis.total_loans ?? loans.length} />
                          <SmallStat label="Open" value={creditAnalysis.total_open_loans ?? '—'} />
                          <SmallStat label="Closed" value={creditAnalysis.total_closed_loans ?? '—'} />
                          <SmallStat label="Performing" value={creditAnalysis.performing_loans ?? '—'} />
                          <SmallStat label="Problematic" value={creditAnalysis.problematic_loans ?? '—'} danger />
                        </div>

                        <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white">
                          <div className="border-b border-gray-200 px-4 py-3">
                            <h3 className="font-semibold text-gray-900">Credit history</h3>
                          </div>

                          {loans.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-[900px] w-full text-left text-sm">
                                <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                  <tr>
                                    <th className="px-4 py-3">Institution</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Performance</th>
                                    <th className="px-4 py-3">Opening balance</th>
                                    <th className="px-4 py-3">Repayment</th>
                                    <th className="px-4 py-3">Tenor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loans.map((loan, index) => (
                                    <tr key={`${loan.institution || 'loan'}-${index}`} className="border-t border-gray-100">
                                      <td className="px-4 py-3 font-semibold text-gray-900">{loan.institution || '—'}</td>
                                      <td className="px-4 py-3 capitalize text-gray-600">{loan.loan_status || '—'}</td>
                                      <td className="px-4 py-3 capitalize text-gray-600">{loan.performance_status || '—'}</td>
                                      <td className="px-4 py-3 text-gray-600">{formatCurrency(loan.opening_balance)}</td>
                                      <td className="px-4 py-3 text-gray-600">{formatCurrency(loan.repayment_amount)}</td>
                                      <td className="px-4 py-3 text-gray-600">{loan.tenor ? `${loan.tenor} months` : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="px-4 py-8 text-center text-sm text-gray-400">No credit facilities found.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {isReviewing && (
                      <div className="border-t border-gray-200 p-5 sm:p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {reviewDecision === 'approved' ? 'Approve application' : 'Reject application'}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              Record the reason for this human underwriting decision.
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={closeReview}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50"
                            aria-label="Close review"
                          >
                            <MdClose />
                          </button>
                        </div>

                        <textarea
                          value={reason}
                          onChange={event => setReason(event.target.value)}
                          placeholder="Enter the reason for your decision..."
                          className="mt-4 min-h-[110px] w-full rounded-xl border border-gray-300 p-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
                        />

                        {error && (
                          <p className="mt-2 text-sm font-medium text-red-600">{error}</p>
                        )}

                        <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                          <button
                            type="button"
                            onClick={closeReview}
                            disabled={loading}
                            className="h-11 rounded-xl border border-gray-300 px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Cancel
                          </button>

                          <button
                            type="button"
                            onClick={submitReview}
                            disabled={loading}
                            className={`h-11 rounded-xl px-5 text-sm font-semibold text-white disabled:opacity-50 ${
                              reviewDecision === 'approved'
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-red-600 hover:bg-red-700'
                            }`}
                          >
                            {loading
                              ? 'Processing...'
                              : reviewDecision === 'approved'
                                ? 'Confirm approval'
                                : 'Confirm rejection'}
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

function Metric({ label, value, capitalize = false }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`mt-1 break-words text-base font-bold text-gray-900 ${capitalize ? 'capitalize' : ''}`}>
        {value}
      </p>
    </div>
  )
}

function SmallStat({ label, value, danger = false }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-bold ${danger ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  )
}
